/**
 * Editing helpers for a MultiChoice element's options and their branches.
 *
 * A MultiChoice keeps two parallel structures: `options` (the labels shown to
 * the caller) and, when branching is on, `branches` ({option, targetPageId}).
 * The canvas builds an edge or a dangling stub for every entry in `branches`,
 * so any branch that no longer corresponds to an option keeps drawing itself.
 *
 * These helpers exist so every edit updates both, in one place.
 */

export interface MultiChoiceBranch {
  option: string;
  targetPageId?: string;
}

export interface MultiChoiceEdit {
  options: string[];
  branches?: MultiChoiceBranch[];
}

/**
 * Rebuilds `branches` so it matches `options` one-for-one and in order:
 * orphans are dropped, missing entries added, and existing targets kept.
 *
 * Matching is by label because that is how the properties panel pairs a Select
 * with its branch. Duplicate labels resolve to the first branch — the pairing
 * is already ambiguous at that point, and collapsing is better than emitting a
 * phantom edge.
 */
export function reconcileBranches(
  options: string[],
  branches?: MultiChoiceBranch[],
): MultiChoiceBranch[] | undefined {
  if (!branches) return undefined;

  const targetByLabel = new Map<string, string | undefined>();
  for (const branch of branches) {
    if (!targetByLabel.has(branch.option)) {
      targetByLabel.set(branch.option, branch.targetPageId);
    }
  }

  return options.map((option) => ({
    option,
    targetPageId: targetByLabel.get(option),
  }));
}

export function withOptionRemoved(
  options: string[],
  branches: MultiChoiceBranch[] | undefined,
  index: number,
): MultiChoiceEdit {
  const nextOptions = options.filter((_, i) => i !== index);
  return { options: nextOptions, branches: reconcileBranches(nextOptions, branches) };
}

export function withOptionRenamed(
  options: string[],
  branches: MultiChoiceBranch[] | undefined,
  index: number,
  label: string,
): MultiChoiceEdit {
  const previous = options[index];
  const nextOptions = options.map((option, i) => (i === index ? label : option));

  // The branch is relabelled before reconciling, so the target survives the
  // rename. Reconciling first would look for a branch under the new label,
  // find none, and silently drop the target.
  const relabelled = branches?.map((branch) =>
    branch.option === previous ? { ...branch, option: label } : branch,
  );

  return { options: nextOptions, branches: reconcileBranches(nextOptions, relabelled) };
}

export function withOptionAdded(
  options: string[],
  branches: MultiChoiceBranch[] | undefined,
  label: string,
): MultiChoiceEdit {
  const nextOptions = [...options, label];
  return { options: nextOptions, branches: reconcileBranches(nextOptions, branches) };
}

/**
 * The options with repeated labels dropped, first occurrence kept.
 *
 * For handing to a Mantine Select, which throws on duplicate values. Options
 * are free text, so a stored element can already contain duplicates.
 */
export function uniqueOptions(options: string[]): string[] {
  return [...new Set(options)];
}

/**
 * A default label for a newly added option that no existing option uses.
 *
 * Numbering by count alone reuses a label once an earlier option is removed:
 * with "Option 1" and "Option 3" left, the count says "Option 3" again.
 */
export function nextOptionLabel(options: string[]): string {
  let n = options.length + 1;
  while (options.includes(`Option ${n}`)) n++;
  return `Option ${n}`;
}
